import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import "./Brands.scss";
import { auth } from "../../firebase";
import Footer from "../../components/Footer";
import Button from "../../components/Button";
import Brand from "../../components/Brand";
import { brandsData } from "../../utils/brands/brands";
import { usePersonasContext } from "../../contexts/PersonasContext";
import { mapPersonas } from "../../helpers/map-personas";
import { FirebaseService } from "../../services/firebase.service";
import { updateBrands } from "../../helpers/update-brands";
import Loader from "../../components/Loader";

const xidBlackLogo = require("../../assets/black-logo.png");
const xidWhiteLogo = require("../../assets/white-logo.png");

export const Brands = () => {
  const [user, loading] = useAuthState(auth);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [areAllSelected, setAreAllSelected] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [brands, setBrands] = useState(brandsData);
  const [isLoading, setIsLoading] = useState(true);
  const { selectedPersonas } = usePersonasContext();
  const navigate = useNavigate();

  const updateBrandsData = async () => {
    const { updatedBrands, updatedBrandNames } = await updateBrands(
      brandsData,
      user?.uid || ""
    );

    setBrands(updatedBrands);
    setSelectedBrands(updatedBrandNames);
    setIsLoading(false);
  };

  const handleResize = () => {
    if (window.innerWidth <= 1200) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
  });

  useEffect(() => {
    if (loading) return;
    if (!user) return navigate("/");
    updateBrandsData();
  }, [user, loading]);

  const selectAll = () => {
    if (areAllSelected) {
      const updatedBrands = brands.map((brand) => {
        return {
          ...brand,
          isSelected: false,
        };
      });

      setSelectedBrands([]);
      setBrands(updatedBrands);
      setAreAllSelected(false);
    } else {
      const selectedBrandNames: string[] = [];
      const updatedBrands = brands.map((brand) => {
        selectedBrandNames.push(brand.name);
        return {
          ...brand,
          isSelected: true,
        };
      });

      setBrands(updatedBrands);
      setAreAllSelected(true);
      setSelectedBrands(selectedBrandNames);
    }
  };

  const search = () => {
    const filteredBrands = brandsData.filter((brand) =>
      brand.name.toLocaleLowerCase().includes(searchInput.toLocaleLowerCase())
    );
    setBrands(filteredBrands);
  };

  const finish = async () => {
    try {
      const { personas, children } = mapPersonas(selectedPersonas);
      await FirebaseService.saveUserPersona({
        personas,
        children,
        brands: selectedBrands,
      });
      navigate("/dashboard");
    } catch (err: any) {
      alert(err.message);
    }
  };

  return (
    <div
      className={`row brands-page page-wrapper ${
        !isMobile ? "desktop-background-image" : ""
      }`}
    >
      <div className="col m4 header-container">
        {isMobile ? (
          <div className="mobile-header">
            <img src={xidWhiteLogo}></img>
          </div>
        ) : (
          <>
            <img src={xidBlackLogo}></img>
            <div className="text-header-bold">Make it yours</div>
            <div className="text-description">
              Customize your experiences and rewards <br />
              by chosing the brands you love.
            </div>
          </>
        )}
        {isMobile && (
          <>
            <div className="text-header-bold">Select your brands</div>
            <div className="text-description">
              Pick a few brands you'd like <br />
              to see experiences from
            </div>
          </>
        )}
      </div>
      <div className={`col m4 ${!isMobile && "section-wrapper"} brands-column`}>
        {isLoading ? (
          <div className="loader">
            <Loader size="big" />
          </div>
        ) : (
          <>
            {isMobile && (
              <div className="search-container">
                <div className="label text-main">Find a brand</div>
                <div className="mobile-search-input-container">
                  <input
                    id="search-input"
                    type="text"
                    placeholder="Nordstrom Rack"
                    onChange={(event) => setSearchInput(event.target.value)}
                  />
                  <Button
                    type="secondary"
                    size="default"
                    onClick={() => search()}
                  >
                    Search
                  </Button>
                </div>
              </div>
            )}
            <div className="brands-header">
              <div className="brands-title text-main">Featured Brands</div>
              <div className="select-all text-description">
                {isMobile ? (
                  <>
                    <label>
                      <input type="checkbox" onClick={selectAll} />
                    </label>
                    Select all
                  </>
                ) : (
                  <>
                    Select all
                    <label>
                      <input type="checkbox" onClick={selectAll} />
                    </label>
                  </>
                )}
              </div>
            </div>
            {!isMobile ? (
              <>
                <div className="row brands-container">
                  {brands.map((brand, index) => {
                    if (index < 5) {
                      return (
                        <div key={brand.name} className="col m2">
                          <Brand
                            logo={brand.icon}
                            text={brand.name}
                            clicked={brand.isSelected}
                            onClick={() => {
                              const name = brand.name;
                              brand.isSelected = !brand.isSelected;

                              if (!selectedBrands.includes(name)) {
                                setSelectedBrands([...selectedBrands, name]);
                              } else {
                                const index = selectedBrands.indexOf(name);
                                selectedBrands.splice(index, 1);
                                setSelectedBrands([...selectedBrands]);
                              }
                            }}
                          ></Brand>
                        </div>
                      );
                    }
                  })}
                </div>
                <div className="row brands-container">
                  {brands.map((brand, index) => {
                    if (index > 4) {
                      return (
                        <div key={brand.name} className="col m2">
                          <Brand
                            logo={brand.icon}
                            text={brand.name}
                            clicked={brand.isSelected}
                            onClick={() => {
                              const name = brand.name;
                              brand.isSelected = !brand.isSelected;

                              if (!selectedBrands.includes(name)) {
                                setSelectedBrands([...selectedBrands, name]);
                              } else {
                                const index = selectedBrands.indexOf(name);
                                selectedBrands.splice(index, 1);
                                setSelectedBrands([...selectedBrands]);
                              }
                            }}
                          ></Brand>
                        </div>
                      );
                    }
                  })}
                </div>
              </>
            ) : (
              <div className="mobile-brands-container">
                {brands.map((brand, index, elements) => {
                  if (index % 2 === 0) {
                    const nextBrand = elements[index + 1]
                      ? elements[index + 1]
                      : undefined;

                    return (
                      <div className="row mobile-brands-row">
                        <div key={brand.name} className="col m6">
                          <Brand
                            logo={brand.icon}
                            text={brand.name}
                            clicked={brand.isSelected}
                            onClick={() => {
                              const name = brand.name;

                              brand.isSelected = !brand.isSelected;
                              if (!selectedBrands.includes(name)) {
                                setSelectedBrands([...selectedBrands, name]);
                              } else {
                                const index = selectedBrands.indexOf(name);
                                selectedBrands.splice(index, 1);
                                setSelectedBrands([...selectedBrands]);
                              }
                            }}
                          ></Brand>
                        </div>
                        <div key={nextBrand?.name} className="col m6">
                          {nextBrand && (
                            <Brand
                              logo={nextBrand.icon}
                              text={nextBrand.name}
                              clicked={nextBrand.isSelected}
                              onClick={() => {
                                const name = nextBrand.name;
                                nextBrand.isSelected = !nextBrand.isSelected;

                                if (!selectedBrands.includes(name)) {
                                  setSelectedBrands([...selectedBrands, name]);
                                } else {
                                  const index = selectedBrands.indexOf(name);
                                  selectedBrands.splice(index, 1);
                                  setSelectedBrands([...selectedBrands]);
                                }
                              }}
                            ></Brand>
                          )}
                        </div>
                      </div>
                    );
                  }
                })}
                <div className="load-more-label">Load more...</div>
              </div>
            )}
            {!isMobile && (
              <>
                <div className="load-more-label">Load more...</div>
                <div className="search-container">
                  <div className="label text-main">Find a brand</div>
                  <input
                    id="search-input"
                    type="text"
                    placeholder="Nordstrom Rack"
                    onChange={(event) => setSearchInput(event.target.value)}
                  />
                  <Button
                    type="secondary"
                    size="default"
                    onClick={() => search()}
                  >
                    Search
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>
      <div className="col m4 buttons-col">
        <div className="buttons-container">
          <Button
            size={isMobile ? "default" : "large"}
            className="btn-outlined"
            outline={true}
            onClick={() => navigate("/dashboard")}
          >
            Skip
          </Button>
          <Button type="secondary" size="large" onClick={finish}>
            Finish
          </Button>
        </div>
        {!isMobile && <Footer />}
      </div>
    </div>
  );
};
